import { createApp } from './main'
import Vue from 'vue'
import 'es6-promise/auto'
import { VueMasonryPlugin } from 'vue-masonry'
import smoothScroll from 'smoothscroll-polyfill'
import ProgressBar from 'views/components/progressbar'
import flatPickr from 'vue-flatpickr-component'
import * as VueAwesomeSwipe from 'vue-awesome-swiper/dist/ssr'
import 'swiper/dist/css/swiper.css'
import 'flatpickr/dist/flatpickr.css'
import VueShareSocial from 'vue-share-social'
import VueAnalytics from 'vue-analytics'
import { detect } from 'detect-browser'
import _isEqual from 'lodash/_baseIsEqual'
import _ from 'lodash/core'
import * as Sentry from '@sentry/vue'
import { shouldBlockVoucherReferrer } from './util/voucher-referrer'
import { getMypageRedirectUrl, getMypageRedirectLogUrl } from './util/mypage-redirect'
import { setCookie, getCookie, delCookie } from './util/methods'
import hntUtils from '@/plugins/hntUtils'
import { rules } from '@/util/validate'
import { handleNetFunnelPageEntry } from '@/util/netFunnel'
import {
  createVoucherEmailLoginCheckUrl,
  shouldForceVoucherEmailLogin,
} from '@/util/voucher-email-entry'
const { app, router, store } = createApp()
const { hosts, externalApi, hntDomain, siteId, api, initials, deployEnv } = require('~/config')
const MobileDetect = require('mobile-detect')
const isDevice = new MobileDetect(window.navigator.userAgent)

const moveToMigratedMypage = (fullPath) => {
  const redirectUrl = getMypageRedirectUrl({
    fullPath,
    referrer: document.referrer,
    hanatourHost: hosts.hanatour,
  })
  if (!redirectUrl) {
    return false
  }

  Sentry.withScope((scope) => {
    // SDK가 자동으로 추가하는 Referer와 탐색 기록에도 인증 정보가 남지 않도록 한다.
    scope.addEventProcessor((event) => ({
      ...event,
      request: { url: `${location.origin}${getMypageRedirectLogUrl(fullPath)}` },
      breadcrumbs: [],
      user: undefined,
    }))
    Sentry.captureEvent({
      message: '(구) 마이페이지 진입',
      level: 'info',
      fingerprint: ['client', 'redirect', 'mypage'],
      extra: {
        from: getMypageRedirectLogUrl(fullPath),
        to: getMypageRedirectLogUrl(redirectUrl),
      },
    })
  })
  location.replace(redirectUrl)
  return true
}

const moveToVoucherEmailLogin = (route) => {
  store.dispatch('logout')
  location.href = createVoucherEmailLoginCheckUrl({
    fullPath: route.fullPath,
    redirectOrigin: location.origin,
    accountHost: hosts.hanatourAccount,
    siteId,
  })
}

const bar = new Vue(ProgressBar).$mount()
Vue.prototype.$bar = bar
// window.browser = detect();
document.body.appendChild(bar.$el)
smoothScroll.polyfill()

if (window.__INITIAL_STATE__) {
  store.replaceState(window.__INITIAL_STATE__)
}

const { GoogleMapsLoader } = require('./plugins/googleLoader')
GoogleMapsLoader.load()

const isOldBrowser = () => {
  const browser = detect()
  const botList = [
    'APIs-Google',
    'Mediapartners-Google',
    'AdsBot-Google-Mobile',
    'AdsBot-Google',
    'Googlebot-Image',
    'Googlebot',
    'Googlebot-News',
    'Googlebot-Video',
    'AdsBot-Google-Mobile-Apps',
    'Bingbot',
    'Slurp',
    'DuckDuckBot',
    'Baiduspider',
    'YandexBot',
    'facebot',
    'facebookexternalhit',
    'ia_archiver',
  ]

  const isBot = new RegExp(botList.join('|')).test(navigator.userAgent)
  const isOldChrome = browser.name === 'chrome' && parseInt(browser.version) < 60
  const isOldIe = browser.name === 'ie' && parseInt(browser.version) < 10

  return !isBot && (isOldChrome || isOldIe)
}

const sentryDsn = externalApi.sentry?.dsn

Sentry.init({
  Vue,
  enabled: Boolean(sentryDsn),
  dsn: sentryDsn,
  attachProps: false, // 개인정보가 들어가는 것이 많아 props 데이터 수집은 하지 않는다.
  tracesSampleRate: 1.0,
  maxBreadcrumbs: 50,
  release: initials.sentry?.release,
  initialScope: (scope) => {
    const { isLogged, loggedUser } = store.state.auth
    if (isLogged) {
      // 인증한 사용자 정보
      scope.setUser({
        id: loggedUser?.hntUid.split('_')?.[0],
        email: loggedUser?.email,
        nickname: loggedUser?.nick,
      })
    }
    // 브라우저 세션 아이디 설정
    scope.setTags({
      bSessionId: getCookie('bSessionId'),
    })

    return scope
  },
  integrations: [Sentry.browserTracingIntegration({ router })],
  ignoreErrors: [
    'Navigation aborted from',
    'document.querySelector(".product-name").textContent',
    "reading 'textContent'",
  ],
})

const routerOnReady = async (route) => {
  if (moveToMigratedMypage(route.fullPath)) {
    return
  }

  if (shouldBlockVoucherReferrer(route, document.referrer)) {
    location.replace(`/${route.params.langCode}/404`)
    return
  }

  // 페이지 진입 netFunnel 처리
  const { shouldStop } = await handleNetFunnelPageEntry(route, app)
  if (shouldStop) {
    history.go(-1)
    return
  }
  // 인증 처리
  if (location.pathname === '/login-social') {
    handleLoginSocial()
    return
  }

  const isAuth = getCookie('auth')
  const isHntUid = getCookie('hnt_uid')

  if (isHntUid && !isAuth) {
    handleApiKey(route)

    return
  }

  let isDeviceRedirecting = false

  if (route.query.hnt_uid) {
    // 로그인 리다이렉트 방지
  } else if (
    route &&
    window &&
    isDevice.mobile() &&
    !getCookie('forceDevice') &&
    !route.query.force
  ) {
    delCookie('forceDevice')
    location.href = `${hosts.userMobile}${route.fullPath}`
    isDeviceRedirecting = true
  } else if (window && isDevice.mobile()) {
    setCookie('forceDevice', true)
    window.forceDevice = 'PC'
  }

  if (isDeviceRedirecting) {
    return
  }

  if (shouldForceVoucherEmailLogin(route)) {
    moveToVoucherEmailLogin(route)
    return
  }

  if (isOldBrowser()) {
    location.href = '/public/update-browser.html'
  }

  router.beforeEach(async (to, from, next) => {
    if (moveToMigratedMypage(to.fullPath)) {
      next(false)
      return
    }

    if (shouldBlockVoucherReferrer(to, document.referrer)) {
      next({ path: `/${to.params.langCode}/404`, replace: true })
      return
    }

    // 페이지 진입 netFunnel 처리
    const { shouldStop } = await handleNetFunnelPageEntry(to, app)
    if (shouldStop) {
      history.go(-1)
      return next(false)
    }

    const toMatched = to.matched || []
    const requireAuth = toMatched.find((matched) => matched.meta.auth)
    const notAllow = toMatched.find(
      (matched) =>
        matched.meta.allow === false || (matched.meta.liveAllow === false && store.state.onLive)
    )
    const onlyAnony = toMatched.find((matched) => matched.meta.onlyAnony === true)

    // client모드에서 제거된 기존 상품상세페이지로 진입할 경우 처리
    // TODO: 서버 로직(server.js)에서 hats-front로 리디렉션 코드가 제거될 때 같이 제거하면 된다.
    const isDeprecatedProductDetail = rules.isDeprecatedProductDetail.test(to.fullPath)
    if (isDeprecatedProductDetail) {
      bar.start()
      const productDetailPath = to.fullPath.replace('/ko/product/', '')
      location.href = `${hosts.user}/product-detail/${productDetailPath}`
      next(false)
      return
    }

    if (notAllow) {
      return app.$$alert(app.$t('common.comingSoon'), {
        callback() {
          next(false)
        },
      })
    }

    if (onlyAnony && getCookie('auth')) {
      return app.$$alert(app.$t('common.accessNotAllow'), {
        callback() {
          next(false)
        },
      })
    }

    if (!to.name || !from.name || to.name !== from.name) {
      bar.start()
    }

    if (to.name === 'city' && from.params.friendlyPath !== to.params.friendlyPath) {
      bar.start()
    }

    if (shouldForceVoucherEmailLogin(to)) {
      moveToVoucherEmailLogin(to)
      next(false)
      return
    }

    if (requireAuth && !getCookie('auth')) {
      return next({
        path: `/${to.params.langCode}/account/signin`,
        query: { returnUrl: to.fullPath },
      })
    }
    next()
  })

  router.beforeResolve((to, from, next) => {
    /**
     * 이전페이지 경로 저장
     * @param {object} prev - 이전 페이지 route 객체
     */
    const setPrevPage = (prev) => {
      window.$nuxt = {
        context: {
          from: {
            path: prev.path || '/ko',
          },
        },
      }
    }

    const finish = () => {
      bar.finish()
      setPrevPage(from)
      next()
    }

    if (to.path === from.path) {
      return finish()
    }

    let diffed = false

    const matched = router.getMatchedComponents(to)
    const prevMatched = router.getMatchedComponents(from)
    const judge = (comp, index) =>
      diffed || (diffed = !_isEqual(prevMatched[index], comp) || !_isEqual(to.params, from.params))
    const activated = matched.filter(judge)
    const asyncDataHooks = _(activated)
      .map((c) => c.asyncData)
      .compact()
      .value()

    if (!asyncDataHooks.length) {
      return finish()
    }

    Promise.all(asyncDataHooks.map((hook) => hook({ app, store, route: to, from })))
      .then(finish)
      .catch((error) => {
        if (error.url) {
          next({ path: error.url })
        } else if (error.status) {
          if (error.status >= 500) {
            next({ path: `/${to.params.langCode}/500` })
          } else if (error.status >= 400) {
            next({ path: `/${to.params.langCode}/404` })
          }
        } else {
          const fromMatched = from.matched || []
          const notAllow = fromMatched.find((matched) => matched.meta.allow === false)
          const onlyAnony = fromMatched.find((matched) => matched.meta.onlyAnony === true)
          const requireAuth = fromMatched.find((matched) => matched.meta.auth)
          const authInfo = getCookie('auth') || null

          if (notAllow || (onlyAnony && authInfo)) {
            setPrevPage()
            next({ path: '/', replace: true })
          } else if (requireAuth && !authInfo) {
            return next({
              path: `/${to.params.langCode}/account/signin`,
              query: { returnUrl: to.fullPath },
              replace: true,
            })
          } else {
            bar.finish()
            next(error)
          }
        }
      })
  })

  router.onError((error) => {
    console.error('Client Side Router Error', {
      method: (error.config && error.config.method) || '',
      status: (error.response && error.response.status) || 500,
      statusText: (error.response && error.response.statusText) || 'Client Side Router Error',
      reqUrl:
        (error.request && error.request.path) ||
        (error.config && error.config.url) ||
        (error.response && error.response.config.url) ||
        '',
      token: app.isLive || (error.config ? error.config.headers.Authorization : null),
      stack: error.stack || '',
    })
  })

  Vue.mixin({
    beforeRouteUpdate(to, from, next) {
      const { asyncData } = this.$options
      if (asyncData) {
        asyncData({
          app,
          store: this.$store,
          route: to,
        })
          .then(() => {
            next()
          })
          .catch((error) => {
            console.error(error)
            next(error)
          })
      } else {
        next()
      }
    },
  })
  Vue.use(flatPickr)
  Vue.use(VueShareSocial)
  Vue.use(VueAwesomeSwipe)
  Vue.use(VueMasonryPlugin, { ssr: false })
  Vue.use(VueAnalytics, {
    id: () => externalApi.googleAnalytic.key,
    router,
    autoTracking: {
      pageviewOnLoad: false,
      page: false,
      disabled: true,
    },
    ecommerce: {
      enabled: true,
    },
  })
  Vue.use(hntUtils, store)

  app.$mount('#app')
}

const promise = Promise.resolve()
window.addEventListener('pageshow', (event) => {
  const loginFlag = history.state && history.state.loginFlag

  if (event.persisted && loginFlag) {
    history.replaceState(null, null, location.href)
    window.location.reload()
  }
})

if (!moveToMigratedMypage(`${location.pathname}${location.search}${location.hash}`)) {
  promise
    .then(() => {
      const auth = getCookie('auth')
      if (auth) {
        const authData = JSON.parse(auth)
        if (authData.tokenExpired < Date.now()) {
          return store.dispatch('refreshAuth', authData.refreshToken)
        } else {
          return authData
        }
      } else {
        return null
      }
    })
    .then((auth) => {
      console.log('entry-client auth: ', auth)
      if (auth === null) {
        return
      }
      return store.dispatch('getUserData')
    })
    .then(() => router.onReady(routerOnReady))
    .catch(() => {
      store.commit('setAuthenticated', false)
      store.commit('setUserData', null)
      delCookie('auth', hntDomain)
      delCookie('autoSave')
      return router.onReady(routerOnReady)
    })
}

if (location.protocol === 'https:' && navigator.serviceWorker) {
  // navigator.serviceWorker.register('/service-worker.js');
}

async function handleLoginSocial() {
  const params = new URLSearchParams(location.search)
  const socialKey = params.get('social')

  try {
    await store.dispatch('loginSocial', socialKey)
    const redirectURL = localStorage.getItem('login_success_path')
    localStorage.removeItem('login_success_path')
    location.replace(redirectURL || '/')
  } catch (error) {
    console.error(error)
  }
}

const EXCLUDE_PATHS = ['/login-social', '/ko/error/login'] // 인증 로직 거치지 않거나 마케팅 로그 전송 제외할 경로들

async function handleApiKey(route) {
  if (EXCLUDE_PATHS.includes(route.path)) {
    return
  }

  localStorage.setItem('login_success_path', route.fullPath)

  const config = {
    headers: { siteId },
    withCredentials: true,
  }

  try {
    const { data } = await app.$http.get(`${hosts.hanatourApi}/chnl/api/apiKey`, config)
    const apiKey = encodeURIComponent(data.apiKey)
    if (data.success) {
      const url = `${
        process.env.API_URL || api.server[deployEnv]
      }/user/social/hanatour/oauth/callback?code=${apiKey}`
      location.href = url
    } else {
      console.log('data.success error : ', data.success)
    }
  } catch (error) {
    if (error && error.status === 417) {
      await store.logout()
      location.href = `${hosts.hanatourAccount}/logout?redirectUri=${encodeURIComponent(
        hosts.userMobile
      )}`
    }
  }
}
